import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface FeaturedSectionProps {
  main: Article;
  side: Article[];
}

export default function FeaturedSection({ main, side }: FeaturedSectionProps) {
  return (
    <section className="px-4 md:px-6 xl:px-10 py-14 md:py-20">
      <div className="max-w-[1320px] mx-auto">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.3em] text-primary-600 mb-2">COVER STORY</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">本期主打</h2>
          </div>
          <Link
            to="/ranking"
            className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer"
          >
            查看全部内容
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-6">
          <article className="group relative rounded-lg overflow-hidden bg-foreground-950 cursor-pointer">
            <Link to={`/article/${main.id}`} className="block">
              <div className="aspect-[16/10] w-full overflow-hidden">
                <img
                  src={main.cover}
                  alt={main.coverAlt}
                  title={`${main.title} - 选美吃瓜`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/45 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <span className="inline-block px-2.5 py-1 text-[11px] rounded bg-accent-500 text-foreground-950 mb-3">
                  {main.category}
                </span>
                <h3 className="font-heading text-xl md:text-2xl font-bold text-background-50 leading-snug">
                  {main.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-background-200 line-clamp-2 max-w-[620px]">
                  {main.excerpt}
                </p>
                <div className="mt-4 flex items-center gap-4 text-xs text-background-300">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line" />
                    {main.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" />
                    {main.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line" />
                    {main.readTime}
                  </span>
                </div>
              </div>
            </Link>
          </article>

          <div className="flex flex-col gap-4">
            {side.map((item) => (
              <article
                key={item.id}
                className="group flex gap-4 p-4 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
              >
                <Link
                  to={`/article/${item.id}`}
                  className="w-24 h-20 shrink-0 rounded-md overflow-hidden bg-background-100"
                >
                  <img
                    src={item.cover}
                    alt={item.coverAlt}
                    title={`${item.title} - 选美吃瓜`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </Link>
                <div className="flex flex-col justify-between">
                  <h3 className="font-heading text-sm font-bold leading-snug text-foreground-950 group-hover:text-primary-700 transition-colors">
                    <Link to={`/article/${item.id}`}>{item.title}</Link>
                  </h3>
                  <span className="text-xs text-foreground-500 mt-2 flex items-center gap-1.5">
                    <i className="ri-time-line" />
                    {item.readTime}
                    <span className="text-foreground-300">·</span>
                    {item.category}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}